import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Globe, Info } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { PageSpeakable } from './SEOSchemas';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_FULL = { us: 'United States', uk: 'United Kingdom', eu: 'European Union', jp: 'Japan', kr: 'South Korea', au: 'Australia' };
const REGION_LABELS = { us: 'US', uk: 'UK', eu: 'EU', jp: 'Japan', kr: 'Korea', au: 'Australia' };

export default function BrandConversionPage() {
  const { slug, subpath } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const pair = subpath;
    if (!pair || !slug) return;
    const parts = pair.split('-to-');
    if (parts.length !== 2) return;
    const [from, to] = parts;

    setLoading(true);
    axios.get(`${API}/brand-convert/${slug}/${from}/${to}`).then(r => {
      setData(r.data);
      const fromName = REGION_FULL[from] || from.toUpperCase();
      const toName = REGION_FULL[to] || to.toUpperCase();
      setCanonical(`/brand/${slug}/${pair}`);
      setMeta(
        `${r.data.brand} Size Conversion: ${fromName} to ${toName}`,
        `Convert ${r.data.brand} sizes from ${fromName} to ${toName}. Complete ${r.data.brand} size chart with measurements and fit guide.`
      );
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug, subpath]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading...</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold mb-4">Not Found</h1><Link to="/brands" className="text-[#C5A028]">Browse brands</Link></div>;

  const fromName = REGION_FULL[data.from] || data.from_name;
  const toName = REGION_FULL[data.to] || data.to_name;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <PageSpeakable name={`${data.brand} ${fromName} to ${toName} Sizes`} />
      <div className="flex gap-3 mb-6 text-sm">
        <Link to={`/brand/${slug}`} className="text-[#78716C] hover:text-[#C5A028] transition-colors flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> {data.brand}</Link>
        <span className="text-[#E8E2D9]">|</span>
        <Link to={`/convert/${data.from}-to-${data.to}`} className="text-[#78716C] hover:text-[#C5A028] transition-colors">{fromName} to {toName} (All Brands)</Link>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-5xl font-light tracking-tighter text-[#1C1917] speakable-intro" data-testid="brand-conv-title">
            {data.brand}: {fromName} <ArrowRight className="inline w-5 h-5 text-[#C5A028]" /> {toName}
          </h1>
          <span className="text-xs bg-[#FAF8F4] px-3 py-1 border border-[#E8E2D9] flex items-center gap-1"><Globe className="w-3 h-3" />{data.country_origin}</span>
        </div>
        <p className="text-[#78716C] text-lg mb-4">Convert your {data.brand} sizes from {fromName} ({data.from?.toUpperCase()}) to {toName} ({data.to?.toUpperCase()}) sizing.</p>

        {data.fit_guide && (
          <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-4 flex gap-3 items-start">
            <Info className="w-5 h-5 text-[#C5A028] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A028] mb-1">{data.brand} Fit Guide</p>
              <p className="text-sm text-[#78716C]">{data.fit_guide}</p>
            </div>
          </div>
        )}
      </div>

      {data.categories?.map((cat, idx) => (
        <div key={idx} className="mb-8">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] mb-2">{cat.name}</h2>
          {cat.fit_note && <p className="text-sm text-[#78716C] mb-3 italic">{cat.fit_note}</p>}
          <div className="overflow-x-auto border border-[#E8E2D9]">
            <table className="w-full text-sm" data-testid={`brand-conv-table-${idx}`}>
              <thead>
                <tr className="bg-[#FAF8F4]">
                  {cat.sizes?.[0] && Object.keys(cat.sizes[0]).filter(k =>
                    ['label', 'us', 'uk', 'eu', 'jp', 'kr', 'au', 'cm', 'chest_cm', 'bust_cm', 'waist_cm', 'hip_cm'].includes(k)
                  ).map(k => (
                    <th key={k} className={`text-left p-3 font-semibold border-b border-[#E8E2D9] ${(k === data.from || k === data.to) ? 'text-[#C5A028] bg-[#FEFCF5]' : 'text-[#1C1917]'}`}>
                      {REGION_LABELS[k] || k.replace(/_/g, ' ').replace(/\bcm\b/, '(cm)').replace(/^\w/, c => c.toUpperCase())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cat.sizes?.map((s, i) => (
                  <tr key={i} className="hover:bg-[#FAF8F4] transition-colors">
                    {Object.entries(s).filter(([k]) =>
                      ['label', 'us', 'uk', 'eu', 'jp', 'kr', 'au', 'cm', 'chest_cm', 'bust_cm', 'waist_cm', 'hip_cm'].includes(k)
                    ).map(([k, v]) => (
                      <td key={k} className={`p-3 border-b border-[#E8E2D9] ${(k === data.from || k === data.to) ? 'font-bold text-[#1C1917] bg-[#FEFCF5]' : 'text-[#78716C]'}`}>{v || '-'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Related */}
      <div className="mt-8 bg-[#FAF8F4] border border-[#E8E2D9] p-6">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mb-4">More {data.brand} Conversions</h3>
        <div className="flex flex-wrap gap-2">
          {Object.keys(REGION_FULL).filter(r => r !== data.from && r !== data.to).map(r => (
            <Link key={r} to={`/brand/${slug}/${data.from}-to-${r}`} className="text-xs text-[#C5A028] hover:underline bg-white px-3 py-1.5 border border-[#E8E2D9]">
              {data.brand} {REGION_FULL[data.from]} to {REGION_FULL[r]}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Link to={`/brand/${slug}`} className="text-sm text-[#C5A028] hover:underline">Full {data.brand} Size Chart</Link>
        <Link to={`/convert/${data.from}-to-${data.to}`} className="text-sm text-[#C5A028] hover:underline">All {fromName} to {toName} Sizes</Link>
        <Link to="/find-my-size" className="text-sm text-[#C5A028] hover:underline">Find My Size</Link>
      </div>

      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />

      <div className="mt-6 text-xs text-[#78716C]"><strong>Disclaimer:</strong> Sizes are approximate. {data.brand} sizes may vary by style and cut. Always verify with {data.brand}'s official size chart. TrueFitGuide is not affiliated with {data.brand}.</div>
    </div>
  );
}
